import React from "react";

const ACTION_LABELS = {
  ASSIGNED: "Assigned",
  REASSIGNED: "Reassigned",
  VERIFIED: "Accepted by employee",
  SPLIT_CREATED: "New batch assigned",
  REJECTED: "Rejected by employee",
};

function describeAssignmentEvent(ev) {
  const from = ev.fromEmployeeName || "Admin / Unassigned";
  const to = ev.toEmployeeName || "Admin / Unassigned";

  if (ev.action === "VERIFIED") {
    return `${to} accepted receipt`;
  }
  if (ev.action === "REJECTED") {
    return `${from} rejected it - returned to the pool`;
  }
  return `${from} → ${to}`;
}

const InventoryHistoryModal = ({ isOpen, onClose, data, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden m-4">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">Inventory History</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto">

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : !data ? (
          <div className="text-center py-12 text-gray-500">
            No history found for this inventory code.
          </div>
        ) : (
          <div className="space-y-8">
            {/* CURRENT STATUS */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                Current Details
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500 block mb-1">Item Name</span>
                  <span className="font-medium text-gray-900">
                    {data.name || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block mb-1">Status</span>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-medium ${
                      data.status === "Assigned"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {data.status || "UNASSIGNED"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block mb-1">Assigned To</span>
                  <span className="font-medium text-gray-900">
                    {data.assignedEmployeeName || "None"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block mb-1">Assigned Date</span>
                  <span className="font-medium text-gray-900">
                    {data.assignedDate
                      ? new Date(Number(data.assignedDate)).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* ASSIGNMENT TIMELINE */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Assignment History ({data.assignmentHistory?.length || 0})
              </h3>

              {data.assignmentHistory && data.assignmentHistory.length > 0 ? (
                <div className="relative border-l border-gray-200 ml-3 space-y-6">
                  {data.assignmentHistory.map((ev, index) => (
                    <div key={index} className="relative pl-6">
                      <span className="absolute left-[-5px] top-1 h-2.5 w-2.5 rounded-full bg-purple-600 ring-4 ring-white"></span>

                      <div className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm hover:shadow-md transition">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-semibold text-gray-800">
                            {ACTION_LABELS[ev.action] || ev.action}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                            {ev.createdAt
                              ? new Date(Number(ev.createdAt)).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
                              : "N/A"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {describeAssignmentEvent(ev)}
                          {ev.quantity ? ` · qty ${ev.quantity}` : ""}
                        </p>
                        {ev.note && (
                          <p className="text-sm text-amber-700 mt-1">
                            {ev.action === "REJECTED" ? "Reason: " : ""}
                            {ev.note}
                          </p>
                        )}
                        <div className="flex gap-2 mt-2">
                          {ev.changedByType && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              By {ev.changedByType === "ADMIN" ? "Admin" : "Employee"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg border border-dashed">
                  No assignment history available.
                </div>
              )}
            </div>

            {/* SCAN TIMELINE */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Scan History ({data.scans?.length || 0})
              </h3>

              {data.scans && data.scans.length > 0 ? (
                <div className="relative border-l border-gray-200 ml-3 space-y-6">
                  {data.scans.map((scan, index) => (
                    <div key={index} className="relative pl-6">
                      {/* Timeline dot */}
                      <span className="absolute left-[-5px] top-1 h-2.5 w-2.5 rounded-full bg-blue-600 ring-4 ring-white"></span>
                      
                      <div className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm hover:shadow-md transition">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-semibold text-gray-800">
                            {scan.serviceName || "Service"}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                            {scan.scannedAt
                              ? new Date(Number(scan.scannedAt)).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })
                              : "N/A"}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <p>Order ID: {scan.orderId}</p>
                          <div className="flex gap-2 mt-2">
                            {scan.scannedByAdmin && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                Admin Scanned
                              </span>
                            )}
                            {scan.forceScanned && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                Force Scanned
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg border border-dashed">
                  No scan history available.
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default InventoryHistoryModal;
